import {DataGrid, GridFilterModel, GridRowSelectionModel, GridSortModel, useGridApiRef} from "@mui/x-data-grid";
import {useEffect, useState} from "react";
import {useListContext} from "../../contexts/ListContext";

import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useEntrezMap} from "../../hooks/useEntrezMap";
import {useFilteredTableData} from "../../hooks/useFilteredTableData";
import {useGeneRefs} from "../../hooks/useGeneRefs";
import {usePostListCorrelations} from "../../hooks/useJsonLoader";
import {Correlations} from "../../types/list";
import {useCorrelationColumns} from "./ListTableColumns";


const ListCorrelations = () => {
  const {currentGene} = useTheCellMapContext();
  const {listState, setSelectedRowModal, setDisplayedTableData} = useListContext();
  const entrezMap = useEntrezMap();
  const geneRefs = useGeneRefs();

  const pccSortModel: GridSortModel = [
    {field: "pcc", sort: "desc"},
  ];

  const apiRef = useGridApiRef();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>(pccSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const {correlationColumns} = useCorrelationColumns(apiRef);

  const postCorrelations = usePostListCorrelations();
  const [data, setData] = useState<Correlations | undefined>();

  useFilteredTableData({data, columnDef: correlationColumns}, filterModel);

  useEffect(() => {
    if (!data || selectionModel.length === 0) return; // We get ref only when data is loaded and grid is ready
    setSelectedRowModal(apiRef.current.getRow(selectionModel[0]));

    return () => {
      setSelectedRowModal(null); // Reset selection when component unmounts so we don't mix up data
    }
  }, [selectionModel, data]);

  useEffect(() => {
    if (!data || !data.correlations.length) return;

    setSelectionModel([apiRef.current.getRowIdFromRowIndex(0)])
  }, [data, apiRef, setSelectionModel]);

  useEffect(() => {
    setData(undefined);

    if (!currentGene || !entrezMap || !geneRefs) return;

    postCorrelations(
      {query: currentGene?.value},
      false
    ).then(result => {
      if (!result) return;

      result.correlations.forEach(correlation => {
        let symbol = entrezMap[correlation.g1_eid].s;
        let useTwoNames = symbol !== correlation.g1_lbl;
        correlation.g1_cell = {
          entrez: correlation.g1_eid,
          symbol: symbol,
          display: useTwoNames ? symbol : correlation.g1_lbl,
          alternate: useTwoNames ? correlation.g1_lbl : null,
          screen: null,
          refs: geneRefs[correlation.g1_eid]
        }

        symbol = entrezMap[correlation.g2_eid].s;
        useTwoNames = symbol !== correlation.g2_lbl;
        correlation.g2_cell = {
          entrez: correlation.g2_eid,
          symbol: symbol,
          display: useTwoNames ? symbol : correlation.g2_lbl,
          alternate: useTwoNames ? correlation.g2_lbl : null,
          screen: null,
          refs: geneRefs[correlation.g2_eid]
        }
      });

      setData(result);
      setDisplayedTableData(result);
      apiRef.current?.setFilterModel({ items: [] }); // Clear filters when data changes
    });

    return () => {
      setData(undefined);
      setDisplayedTableData(null);
    }
  }, [currentGene, entrezMap, geneRefs, listState.dataType]);

  // Handler to allow only single-column sorting
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setSortModel([model[model.length - 1]]);
    } else {
      setSortModel([]);
    }
  };

  return (
    <DataGrid
      autoHeight={false}
      className="border-0"
      columns={correlationColumns}
      disableColumnMenu
      disableColumnFilter
      rows={data ? data.correlations : []}
      sortModel={sortModel}
      sortingOrder={['asc', 'desc']}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={setSelectionModel}
      onSortModelChange={handleSortModelChange}
      onFilterModelChange={setFilterModel}
      apiRef={apiRef}
      localeText={{
        noRowsLabel: (currentGene && !entrezMap?.[currentGene.value].l)
          ? 'Selected gene is not present in the TKOv3 library.'
          : !currentGene ? 'Search genes to view profile similarities.'
            : data ? 'No significant profile similarity data.' : 'Loading...',
      }}
    />
  )
}
export default ListCorrelations;
